/**
 * PotsService
 * @module Services/domain/PotsService
 * @description Offers a set of methods to help performing pots related actions
 */

import HttpService from "../http/HttpService";
import LinksProvider from "../http/LinksProvider";
import ErrorHandler from "../error/ErrorHandler";
import {PotCategory, PotStatus} from "../../models/Pot";
import {convertFromRaw, convertToRaw, EditorState} from "draft-js";
import _ from "lodash";
import htmlToText from "html-to-text";
import {stateToHTML} from "draft-js-export-html";

const self = {};

/**
 * Creates a pot
 * @author Sameh Bellez
 * @alias create
 * @param request {string} the request payload
 * @memberof module:Services/domain/PotsService
 * @returns {promise}  A promise containing the creation result
 */
self.create = (request) => {
  return HttpService().post(LinksProvider.get(LinksProvider.API.POTS.POTS), request)
    .then(result => result.pot);
};

/**
 * Updates a pot
 * @author Sameh Bellez
 * @alias update
 * @param request {string} the request payload
 * @memberof module:Services/domain/PotsService
 * @returns {promise}  A promise containing the update result
 */
self.update = (reference, request) => {
  return HttpService().put(LinksProvider.get(LinksProvider.API.POTS.POT, {reference: reference}), request)
    .then(result => result.pot);
};

/**
 * Cancels a giveaway request
 * @author Hassen CHAREF
 * @alias cancelGiveawayRequest
 * @param reference {string} the pot reference
 * @memberof module:Services/domain/PotsService
 * @returns {promise}  A promise containing cancel confirmation
 */
self.cancelGiveawayRequest = (reference) => {
  return HttpService().delete(LinksProvider.get(LinksProvider.API.POTS.CANCEL_GIVEAWAY_REQUEST, {reference: reference}))
    .then(result => result);
};

/**
 * Updates a pot
 * @author Sameh Bellez
 * @alias update
 * @param reference {string} the request payload
 * @param request {object} object containing request bo
 * @memberof module:Services/domain/PotsService
 * @returns {promise}  A promise containing the update result
 */
self.updateStatus = (reference, request) => {
  return HttpService().put(LinksProvider.get(LinksProvider.API.POTS.UPDATE_STATUS, {reference: reference}), request)
    .then(result => result.pot);
};

/**
 * Gets a pot
 * @author Sameh Bellez
 * @alias get
 * @param reference {string} The pot reference
 * @memberof module:Services/domain/PotsService
 * @returns {promise}  A promise containing the resulting pot
 */
self.get = (reference, ctx) => {
  return HttpService(ctx).get(LinksProvider.get(LinksProvider.API.POTS.POT, {reference: reference}))
    .then(result => result.pot);
};

/**
 * Gets a pot and redirects if not found
 * @author Hassen Charef
 * @alias get
 * @param reference {string} The pot reference
 * @memberof module:Services/domain/PotsService
 * @returns {promise}  A promise containing the resulting pot
 */
self.getAndRedirect = async (reference, ctx) => {
  return await HttpService(ctx).get(LinksProvider.get(LinksProvider.API.POTS.POT, {reference: reference}))
    .then(result => {
      return result.pot;
    })
    .catch(error => {
      if (error.code === "E_NOT_FOUND") {
        ErrorHandler.addCookieErrorMessage('common:errors.E_POT_NOT_FOUND', ctx);
      } else {
        ErrorHandler.addCookieErrorMessage('common:errors.DEFAULT_ERROR_MESSAGE', ctx);
      }
      ctx.res.writeHead(302, {
        Location: LinksProvider.ROUTES.HOME
      });
      ctx.res.end();
    });
};

/**
 * Gets a pot benficiary
 * @author Paris Romain
 * @alias get
 * @param reference {string} The pot reference
 * @memberof module:Services/domain/PotsService
 * @returns {promise}  A promise containing the resulting pot
 */
self.getBeneficiary = (reference, ctx) => {
  return HttpService(ctx).get(LinksProvider.get(LinksProvider.API.POTS.POT_BENEFICIARY, {reference: reference}))
    .then(result => result);
};

/**
 * Send message to pot organizer
 * @author Seif Khiari
 * @alias contactOrganizer
 * @param reference {string} The pot reference
 * @param request {string} the request data
 * @memberof module:Services/domain/PotsService
 * @returns {promise}  A promise containing the result data of the sent email
 */
self.contactOrganizer = (reference, request) => {
  return HttpService().post(LinksProvider.get(LinksProvider.API.POTS.CONTACT_ORGANIZER, {reference: reference}), request);
};

/**
 * Thank pot participants
 * @author Seif Khiari
 * @alias thankParticipants
 * @param reference {string} The pot reference
 * @param request {string} the request data
 * @memberof module:Services/domain/PotsService
 * @returns {promise}  A promise containing the result data of the sent email
 */
self.thankParticipants = (reference, request) => {
  return HttpService().post(LinksProvider.get(LinksProvider.API.POTS.THANK_PARTICIPANTS, {reference: reference}), request);
};

/**
 * pot offer request
 * @author Romain Paris
 * @alias create
 * @param reference {string} the pot reference
 * @param request {object} the request payload
 * @memberof module:Services/domain/PotsService
 * @returns {promise}  A promise containing the request result
 */
self.createCashOut = (reference, request) => {
  return HttpService().post(LinksProvider.get(LinksProvider.API.POTS.CASH_OUT, {reference: reference}), request)
    .then(result => result.cash_out);
};

/**
 * pot offer request
 * @author Romain Paris
 * @alias get
 * @param reference {string} the pot reference
 * @param cashoutReference {string} the cashout reference
 * @memberof module:Services/domain/PotsService
 * @returns {promise}  A promise containing the request result
 */
self.getCashOut = (reference, cashOutReference, ctx) => {
  return HttpService(ctx).get(LinksProvider.get(LinksProvider.API.POTS.CASH_OUT_CONFIRM, {
    reference: reference, cashOutReference: cashOutReference
  }))
    .then(result => result.cash_out);
};
/**
 * pot offer request
 * @author Romain Paris
 * @alias create
 * @param request {string} the request payload
 * @memberof module:Services/domain/PotsService
 * @returns {promise}  A promise containing the request result
 */
self.offerRequest = (reference, request) => {
  return HttpService().post(LinksProvider.get(LinksProvider.API.POTS.OFFER_REQUEST, {reference: reference}), request)
    .then(result => result.data);
};

/**
 * Confirm offer request
 * @author Romain Paris
 * @alias confirmOfferRequest
 * @memberof module:Services/domain/PotsService
 * @returns {promise}  A promise containing the validation request
 */
self.confirmOfferRequest = function (ctx, potReference, reference, request) {
  return HttpService(ctx).get(LinksProvider.get(LinksProvider.API.POTS.GIVEAWAY_CONFIRM, {
    potReference: potReference, reference: reference
  }), request)
    .then((response) => {
      return response;
    });
};

/**
 * confirm user access
 * @author Romain Paris
 * @alias confirmAccess
 * @memberof module:Services/domain/PotsService
 * @returns {Boolean} boolean confirming user access to pot
 */
self.confirmAccess = function (ctx, pot) {
  if (pot && pot.category === PotCategory.WEDDING_LIST) {
    ctx.res.writeHead(302, {
      Location: LinksProvider.getFullM1lRoute(LinksProvider.ROUTES.M1L.LIST_PAGE, {reference: pot.reference})
    });
    ctx.res.end();
    return false;
  }
  const is_pot_owner = (!pot.is_offered && pot.is_mine) || (pot.is_offered && pot.is_beneficiary);
  const is_pot_archived = pot.status === PotStatus.ARCHIVED;
  if (!is_pot_owner || is_pot_archived) {
    ErrorHandler.addCookieErrorMessage('common:errors.E_PAGE_ACCESS_DENIED', ctx);
    ctx.res.writeHead(302, {
      Location: LinksProvider.get(LinksProvider.ROUTES.POT.POT, {reference: pot.reference})
    });
    ctx.res.end();
    return false;
  } else {
    return true;
  }
};

/**
 * check if promocode is consumed
 * @author Sameh Bellez
 * @alias isPromoCodeConsumed
 * @memberof module:Services/domain/PotsService
 * @param {object} pot
 * @returns {boolean}  whether the promo code is consumed or not
 */
self.isPromoCodeConsumed = function (pot) {
  return !pot.promo_code_data || (pot.promo_code_data && (pot.promo_code_consume_date
    && pot.spent_amount
    > pot.collected_amount));
};

/**
 * Gets the gift card spendable amount
 * @author Sameh Bellez
 * @alias getGiftCardSpendableAmount
 * @memberof module:Services/domain/PotsService
 * @param {object} pot
 * @returns {number}  the gift card spendable amount
 */
self.getGiftCardSpendableAmount = function (pot) {
  return pot.total_amount_left + pot.max_bonus_amount;
};

/**
 * Gets the gift card spendable amount bonus
 * @author Sameh Bellez
 * @alias getGiftCardSpendableBonus
 * @memberof module:Services/domain/PotsService
 * @param {object} pot
 * @returns {number}  the gift card spendable amount bonus
 */
self.getGiftCardSpendableBonus = function (pot) {
  return pot.max_bonus_amount;
};

/**
 * Gets the cash out spendable amount
 * @author Sameh Bellez
 * @alias getCashOutSpendableAmount
 * @memberof module:Services/domain/PotsService
 * @param {object} pot
 * @returns {number}  the cash out spendable amount
 */
self.getCashOutSpendableAmount = function (pot) {
  return pot.amount_max_cashout;
};

/**
 * Gets the pot's description
 * @author Ghassen Manai
 * @alias getPorDescription
 * @memberof module:Services/domain/PotsService
 * @param {object} pot
 * @param {object} t i18n translation object
 * @returns {string}  the pot's description
 */
self.getPotDescription = function (pot, t) {
  if (!pot.description) {
    return t(`pot:description.${pot.category}`);
  }
  const content = EditorState.createWithContent(convertFromRaw(pot.description)).getCurrentContent();
  const rawState = convertToRaw(content);
  if (!rawState || _.isEmpty(rawState)) {
    return true;
  }
  const contentState = convertFromRaw(rawState);

  return !(contentState.hasText() && (contentState.getPlainText().trim()
    !== '')) ? t(`pot:description.${pot.category}`) : htmlToText.fromString(stateToHTML(convertFromRaw(pot.description)), {
    wordwrap: false
  });
};

export default self;
